"use client";
// «💡 Te falta poquito»: cada sugerencia de promoción se abre en una ventana flotante que enseña cómo conseguirla.
// Tours que faltan → tarjetas para elegir el siguiente (este tour se guarda en tu visita y se paga todo junto).
// Personas que faltan → un botón que las suma ahí mismo. Nivel → cuánto falta para subir.
import { useEffect, useMemo, useRef } from "react";
import { tours } from "@/content/catalogo";
import type { Pista } from "@/lib/promociones";
import { usePreciosVigentes } from "@/components/usePreciosVigentes";
import { useContenidoTours } from "@/components/useContenidoTours";
import { formatoMXN } from "@/lib/dinero";
import { Img } from "@/components/Img";

type Props = {
  pista: Pista;
  idioma: "es" | "en";
  slugActual: string;
  enVisita: string[];
  fecha: string;
  adultos: number;
  maxPersonas: number;
  cerrar: () => void;
  sumarPersonas: (n: number) => void;
  /** Guarda el tour actual en la visita antes de ir por el siguiente; devuelve false si no se pudo. */
  guardarEsteTour: () => boolean;
};

export function PistaPromo({ pista, idioma, slugActual, enVisita, fecha, adultos, maxPersonas, cerrar, sumarPersonas, guardarEsteTour }: Props) {
  const es = idioma === "es";
  const ref = useRef<HTMLDialogElement>(null);
  const precios = usePreciosVigentes(), contenido = useContenidoTours();
  useEffect(() => { const d = ref.current; if (d && !d.open) d.showModal(); }, []);

  const opciones = useMemo(() => {
    if (pista.tipo !== "tours") return [];
    const ya = new Set([slugActual, ...enVisita]);
    const pedidos = pista.tours?.length ? pista.tours : tours.map((t) => t.slug);
    return pedidos.filter((s) => !ya.has(s)).map((s) => tours.find((t) => t.slug === s)).filter((t): t is (typeof tours)[number] => !!t).slice(0, 6).map((t) => {
      const c = contenido?.[t.slug];
      return {
        slug: t.slug,
        nombre: (es ? c?.nombre.es : c?.nombre.en) || t.nombre[idioma],
        imagen: c?.imagenes[0]?.archivo ?? t.imagenes[0]?.archivo ?? "",
        precio: precios?.[t.slug]?.adultoCentavos ?? t.adultoCentavos,
        duracion: t.duracionMin,
      };
    });
  }, [pista, slugActual, enVisita, contenido, precios, es, idioma]);

  const ir = (slug: string) => {
    if (!guardarEsteTour()) return;
    location.assign(`/${idioma}/tours/${slug}?fecha=${fecha}&adultos=${adultos}#reservar`);
  };
  const cabenPersonas = pista.personas ? Math.max(0, Math.min(pista.personas, maxPersonas)) : 0;
  const titulo = pista.tipo === "tours" ? (es ? "¡Te falta un tour para ahorrar más!" : "One more tour and you save more!")
    : pista.tipo === "personas" ? (es ? "¡Súmale gente y ahorran todos!" : "Bring more people and everyone saves!")
    : (es ? "Ya casi subes de nivel" : "Almost at the next level");

  return (
    <dialog ref={ref} className="pista" aria-labelledby="pista-t" onClose={cerrar} onClick={(e) => { if (e.target === e.currentTarget) ref.current?.close(); }}>
      <div className="pista-caja">
        <button type="button" className="pista-cerrar" aria-label={es ? "Cerrar" : "Close"} onClick={() => ref.current?.close()}>×</button>
        <header className="pista-cabeza">
          <video src="/media/s_festeja.mp4" poster="/media/cleto_festeja.webp" autoPlay muted loop playsInline aria-hidden="true" />
          <div>
            <h2 id="pista-t">{titulo}</h2>
            <p>{pista.texto}</p>
            {pista.porciento ? <span className="pista-premio">{es ? `Hasta ${pista.porciento}% menos` : `Up to ${pista.porciento}% off`}</span> : null}
          </div>
        </header>

        {pista.tipo === "tours" && (opciones.length ? <>
          <ul className="pista-tours">
            {opciones.map((t, i) => (
              <li key={t.slug} style={{ ["--i" as string]: i }}>
                <button type="button" onClick={() => ir(t.slug)}>
                  {t.imagen && <Img src={t.imagen} alt="" loading="lazy" />}
                  <span className="pista-tour-cuerpo">
                    <b>{t.nombre}</b>
                    <small>{t.duracion ? `${Math.round(t.duracion / 60 * 10) / 10} h · ` : ""}{es ? "desde" : "from"} {formatoMXN(t.precio, idioma)}</small>
                    <span className="pista-tour-ir">{es ? "Elegir horario" : "Pick a time"}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="pista-nota">{es ? "Guardamos este tour en tu visita y te llevamos al otro. Al final pagas todo junto, con el descuento." : "We keep this tour in your visit and take you to the next one. You pay everything together, with the discount."}</p>
        </> : <p className="pista-nota">{es ? "Ya tienes en tu visita todos los tours de esta promoción." : "Your visit already has every tour in this deal."}</p>)}

        {pista.tipo === "personas" && <div className="pista-personas">
          <div className="pista-gente" aria-hidden="true">{Array.from({ length: Math.min(8, adultos) }, (_, i) => <span key={`a${i}`} className="ya" />)}{Array.from({ length: Math.max(0, Math.min(4, pista.personas ?? 0)) }, (_, i) => <span key={`n${i}`} className="nueva" style={{ ["--i" as string]: i }} />)}</div>
          {cabenPersonas > 0
            ? <button type="button" className="boton" onClick={() => { sumarPersonas(cabenPersonas); ref.current?.close(); }}>{es ? `Sumar ${cabenPersonas} persona${cabenPersonas === 1 ? "" : "s"}` : `Add ${cabenPersonas} ${cabenPersonas === 1 ? "person" : "people"}`}</button>
            : <p className="pista-nota">{es ? "Ya no caben más personas en este horario: prueba otra hora." : "No more room at this time: try another departure."}</p>}
        </div>}

        {pista.tipo === "nivel" && <p className="pista-nota">{es ? "Cada tour que pagas cuenta como una visita. Al subir de nivel, el descuento se aplica solo en tus siguientes reservas con el mismo WhatsApp." : "Every paid tour counts as a visit. When you level up, the discount applies automatically to your next bookings with the same phone."}</p>}
      </div>
    </dialog>
  );
}
